import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  ArrowLeft, Bookmark, Trash2, ExternalLink,
  Pencil, Check, X
} from "lucide-react";
import { API, useAuth, authFetch } from "@/App";

export default function Bookmarks() {
  const navigate = useNavigate();
  useAuth();
  const [bookmarks, setBookmarks] = useState([]);
  const [surahs, setSurahs] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editingNote, setEditingNote] = useState("");
  const [savingId, setSavingId] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [bookmarksRes, surahsRes] = await Promise.all([
        authFetch(`${API}/bookmarks`),
        fetch(`${API}/quran/surahs`)
      ]);

      if (bookmarksRes.ok) {
        const data = await bookmarksRes.json();
        setBookmarks(data);
      }

      if (surahsRes.ok) {
        const data = await surahsRes.json();
        const surahMap = {};
        data.forEach(s => { surahMap[s.number] = s; });
        setSurahs(surahMap);
      }
    } catch (error) {
      console.error('Error fetching bookmarks:', error);
      toast.error('Failed to load bookmarks');
    } finally {
      setIsLoading(false);
    }
  };

  const deleteBookmark = async (bookmarkId) => {
    try {
      const res = await authFetch(`${API}/bookmarks/${bookmarkId}`, {
        method: 'DELETE'
      });

      if (res.ok) {
        setBookmarks(prev => prev.filter(b => b.bookmark_id !== bookmarkId));
        toast.success('Bookmark deleted');
      }
    } catch (error) {
      console.error('Error deleting bookmark:', error);
      toast.error('Failed to delete bookmark');
    }
  };

  const startEdit = (b) => {
    setEditingId(b.bookmark_id);
    setEditingNote(b.note || "");
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditingNote("");
  };

  const saveNote = async (bookmarkId) => {
    setSavingId(bookmarkId);
    try {
      const res = await authFetch(`${API}/bookmarks/${bookmarkId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ note: editingNote })
      });
      if (res.ok) {
        const updated = await res.json();
        setBookmarks(prev =>
          prev.map(b => b.bookmark_id === bookmarkId ? { ...b, note: updated.note } : b)
        );
        toast.success('Note saved');
        cancelEdit();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to save note');
      }
    } catch (error) {
      console.error('Error saving note:', error);
      toast.error('Failed to save note');
    } finally {
      setSavingId(null);
    }
  };

  const getSurahName = (surahNum) => surahs[surahNum]?.englishName || `Surah ${surahNum}`;
  const getSurahArabicName = (surahNum) => surahs[surahNum]?.name || '';

  if (isLoading) {
    return (
      <div className="min-h-screen bg-islamic-blue-950 flex items-center justify-center">
        <div className="spinner w-12 h-12"></div>
      </div>
    );
  }

  return (
    <div data-testid="bookmarks-page" className="min-h-screen bg-islamic-blue-950">
      <header className="glass border-b border-white/5 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <button
            data-testid="back-btn"
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-2 text-slate-400 hover:text-gold-400 transition-colors font-outfit"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back</span>
          </button>

          <div className="flex items-center gap-3">
            <Bookmark className="w-5 h-5 text-gold-400" />
            <h1 className="font-cinzel text-lg font-semibold text-slate-100">Bookmarks</h1>
          </div>

          <div className="w-16"></div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8">
        {bookmarks.length === 0 ? (
          <div className="text-center py-20">
            <div className="w-20 h-20 rounded-full bg-gold-400/10 flex items-center justify-center mx-auto mb-6">
              <Bookmark className="w-10 h-10 text-gold-400/50" />
            </div>
            <h2 className="font-cinzel text-xl text-slate-100 mb-2">No Bookmarks Yet</h2>
            <p className="text-slate-500 font-outfit mb-6">
              Save verses you want to revisit later
            </p>
            <Button
              data-testid="browse-surahs-btn"
              onClick={() => navigate('/dashboard')}
              className="btn-gold rounded-full px-6"
            >
              Browse Surahs
            </Button>
          </div>
        ) : (
          <ScrollArea className="h-[calc(100vh-150px)]">
            <div className="space-y-4">
              {bookmarks.map((bookmark) => {
                const isEditing = editingId === bookmark.bookmark_id;
                return (
                  <div
                    key={bookmark.bookmark_id}
                    data-testid={`bookmark-${bookmark.bookmark_id}`}
                    className="glass-card rounded-xl p-5 card-lift"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 mb-2 flex-wrap">
                          <span className="w-auto px-2 h-8 rounded-lg bg-gold-400/10 flex items-center justify-center text-gold-400 text-sm font-outfit">
                            {bookmark.surah_number}:{bookmark.ayah_number}
                          </span>
                          <div>
                            <h3 className="font-outfit font-medium text-slate-100">
                              {getSurahName(bookmark.surah_number)}
                            </h3>
                            <p className="text-slate-500 text-xs font-outfit">
                              Ayah {bookmark.ayah_number}
                            </p>
                          </div>
                          <p className="font-amiri text-gold-400/80 ml-auto hidden sm:block">
                            {getSurahArabicName(bookmark.surah_number)}
                          </p>
                        </div>

                        {/* Note section: editing OR display */}
                        {isEditing ? (
                          <div className="mt-3 pl-1 sm:pl-11">
                            <Textarea
                              data-testid={`note-input-${bookmark.bookmark_id}`}
                              value={editingNote}
                              onChange={(e) => setEditingNote(e.target.value)}
                              placeholder="Add a personal note for this verse…"
                              maxLength={1000}
                              className="bg-islamic-blue-950/60 border-white/10 text-slate-200 placeholder:text-slate-600 min-h-[80px] font-outfit"
                              autoFocus
                            />
                            <div className="flex items-center justify-between mt-2">
                              <span className="text-slate-600 text-xs font-outfit">
                                {editingNote.length}/1000
                              </span>
                              <div className="flex gap-2">
                                <Button
                                  data-testid={`cancel-note-${bookmark.bookmark_id}`}
                                  variant="ghost"
                                  size="sm"
                                  onClick={cancelEdit}
                                  className="text-slate-400 hover:text-slate-200 h-8"
                                >
                                  <X className="w-4 h-4 mr-1" /> Cancel
                                </Button>
                                <Button
                                  data-testid={`save-note-${bookmark.bookmark_id}`}
                                  size="sm"
                                  disabled={savingId === bookmark.bookmark_id}
                                  onClick={() => saveNote(bookmark.bookmark_id)}
                                  className="btn-gold h-8"
                                >
                                  <Check className="w-4 h-4 mr-1" />
                                  {savingId === bookmark.bookmark_id ? "Saving…" : "Save"}
                                </Button>
                              </div>
                            </div>
                          </div>
                        ) : bookmark.note ? (
                          <p
                            data-testid={`note-display-${bookmark.bookmark_id}`}
                            className="text-slate-300 text-sm font-outfit mt-3 pl-1 sm:pl-11 whitespace-pre-wrap border-l-2 border-gold-400/30 sm:border-l-0 pl-3 sm:pl-11"
                          >
                            {bookmark.note}
                          </p>
                        ) : (
                          <button
                            data-testid={`add-note-${bookmark.bookmark_id}`}
                            onClick={() => startEdit(bookmark)}
                            className="text-slate-500 text-sm font-outfit mt-3 sm:ml-11 hover:text-gold-400 italic"
                          >
                            + Add a note
                          </button>
                        )}

                        <p className="text-slate-600 text-xs font-outfit mt-3 pl-1 sm:pl-11">
                          Saved {new Date(bookmark.created_at).toLocaleDateString()}
                        </p>
                      </div>

                      <div className="flex flex-col sm:flex-row items-center gap-1 sm:gap-2 shrink-0">
                        {!isEditing && bookmark.note && (
                          <Button
                            data-testid={`edit-note-${bookmark.bookmark_id}`}
                            variant="ghost"
                            size="icon"
                            onClick={() => startEdit(bookmark)}
                            className="text-slate-400 hover:text-gold-400 hover:bg-gold-400/10"
                            title="Edit note"
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                        )}
                        <Button
                          data-testid={`go-to-bookmark-${bookmark.bookmark_id}`}
                          variant="ghost"
                          size="icon"
                          onClick={() => navigate(`/reader/${bookmark.surah_number}`)}
                          className="text-slate-400 hover:text-gold-400 hover:bg-gold-400/10"
                          title="Open in reader"
                        >
                          <ExternalLink className="w-4 h-4" />
                        </Button>
                        <Button
                          data-testid={`delete-bookmark-${bookmark.bookmark_id}`}
                          variant="ghost"
                          size="icon"
                          onClick={() => deleteBookmark(bookmark.bookmark_id)}
                          className="text-slate-400 hover:text-red-400 hover:bg-red-400/10"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </ScrollArea>
        )}
      </main>
    </div>
  );
}
